'use client';

import { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ArrowLeft,
  LogOut,
  Shield,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase, adminAuth } from '@/lib/supabase';
import AdminBookings from '@/components/AdminBookings';
import AdminCalendar from '@/components/AdminCalendar';

interface AdminDashboardProps {
  onLogout: () => void;
  adminData: any;
}

const AdminDashboard = ({ onLogout, adminData }: AdminDashboardProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState({
    bookings: [] as any[],
    totalRevenue: 0,
    occupancyRate: 0,
    recentActivity: [] as any[]
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load bookings
      const { data: bookings } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false });

      // Calculate stats
      const confirmedBookings = bookings?.filter(b => b.status === 'confirmed') || [];
      const totalRevenue = confirmedBookings.reduce((sum, b) => sum + parseFloat(b.total_price), 0);
      
      // Calculate occupancy rate (simplified)
      const totalDays = 30; // Last 30 days
      const bookedDays = confirmedBookings.reduce((sum, b) => {
        const checkIn = new Date(b.check_in);
        const checkOut = new Date(b.check_out);
        const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
        return sum + nights;
      }, 0);
      const occupancyRate = Math.round((bookedDays / totalDays) * 100);

      setDashboardData({
        bookings: bookings || [],
        totalRevenue,
        occupancyRate,
        recentActivity: bookings?.slice(0, 5) || []
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminAuth.signOut();
      onLogout();
    } catch (error) {
      console.error('Error signing out:', error);
      onLogout(); // Logout anyway
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Admin Access Banner */}

      {/* Recent Bookings */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#7C8F75]/10 overflow-hidden overflow-x-auto">
        <div className="p-6 border-b border-[#7C8F75]/10">
          <h3 className="text-xl font-bold text-[#333333]">Recent Bookings</h3>
        </div>
        <div className="min-w-full overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-[#F9F6F2]">
              <tr>
                <th className="text-left p-3 sm:p-4 text-[#333333] font-semibold min-w-[150px]">Guest</th>
                <th className="text-left p-3 sm:p-4 text-[#333333] font-semibold min-w-[180px]">Dates</th>
                <th className="text-left p-3 sm:p-4 text-[#333333] font-semibold min-w-[100px]">Amount</th>
                <th className="text-left p-3 sm:p-4 text-[#333333] font-semibold min-w-[120px]">Status</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.recentActivity.map((booking: any) => (
                <tr key={booking.id} className="border-b border-[#7C8F75]/10 hover:bg-[#F9F6F2]/50">
                  <td className="p-3 sm:p-4">
                    <div>
                      <p className="font-semibold text-[#333333] text-sm sm:text-base">{booking.guest_name}</p>
                      <p className="text-sm text-[#333333]/60">{booking.guest_email}</p>
                    </div>
                  </td>
                  <td className="p-3 sm:p-4 text-[#333333] text-sm sm:text-base">
                    {new Date(booking.check_in).toLocaleDateString()} - {new Date(booking.check_out).toLocaleDateString()}
                  </td>
                  <td className="p-3 sm:p-4 font-semibold text-[#333333] text-sm sm:text-base">${booking.total_price}</td>
                  <td className="p-3 sm:p-4">
                    <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}>
                      {getStatusIcon(booking.status)}
                      <span className="capitalize">{(booking as any).status}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9F6F2] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7C8F75]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F2]">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-[#7C8F75]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-[#333333] hover:text-[#7C8F75] hover:bg-[#7C8F75]/10"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Site
              </Button>
              <div className="h-6 w-px bg-[#7C8F75]/20"></div>
              <h1 className="text-2xl font-bold text-[#333333]">Mountain View Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <Shield className="h-4 w-4 mr-1 inline" />
                  Admin
                </span>
              </div>
              <span className="text-sm text-[#333333]/60">Welcome, {adminData?.admin?.email}</span>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-[#7C8F75] text-[#7C8F75] hover:bg-[#7C8F75] hover:text-white"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-[#7C8F75]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#7C8F75] text-[#7C8F75]'
                    : 'border-transparent text-[#333333]/60 hover:text-[#333333] hover:border-[#7C8F75]/30'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'bookings' && (
          <AdminBookings 
            sessionToken={adminData?.session?.access_token || adminData?.user?.access_token || ''} 
            onDataChange={loadDashboardData}
          />
        )}
        {activeTab === 'calendar' && (
          <AdminCalendar sessionToken={adminData?.sessionToken || adminData?.session?.access_token || ''} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;